import { useRef } from 'react';
import { ATTACHMENT_MAX_BYTES, attachmentKindFor } from '@supporchat/shared';
import { formatBytes } from '../lib/format';

/**
 * Escolhe um arquivo para mandar ao cliente.
 *
 * O atendimento já recebia foto e vídeo e só podia responder em texto ou voz — o
 * que obriga a descrever com palavras o que uma foto resolve ("é o parafuso do
 * lado direito, o de cabeça sextavada"). Também é por aqui que sai o manual em
 * PDF e a nota fiscal, hoje enviados pelo WhatsApp pessoal de quem atende, fora
 * do histórico do ticket.
 *
 * O arquivo escolhido não vai embora na hora: ele fica em espera no compositor,
 * onde o atendente confere o que pegou e escreve a legenda. Anexo que sai no
 * clique é anexo errado enviado ao cliente errado.
 */
export function AttachmentPicker({
  disabled,
  onPick,
  onError,
}: {
  disabled: boolean;
  onPick: (file: File) => void;
  onError: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={input}
        type="file"
        // Sem `accept` de propósito. Ele filtra a janela de arquivos por tipo, e
        // o tipo que cada sistema associa a uma extensão varia: no Windows o
        // `.zip` é `application/x-zip-compressed` e ficava cinza na janela, o
        // `.json` nem aparecia. Documento é lista aberta; o que é recusado
        // (página web, executável) é dito em `attachmentProblem`, com o motivo.
        // Escondido porque quem aparece é o botão ao lado, que o aciona pela
        // ref: o campo de arquivo nativo não aceita estilo e não combinaria com
        // o resto do compositor. O botão é que carrega foco e rótulo.
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Zera o valor sempre: sem isso, escolher o mesmo arquivo duas vezes
          // seguidas não dispara `change` na segunda.
          event.target.value = '';
          if (!file) return;

          const problem = attachmentProblem(file);
          if (problem) {
            onError(problem);
            return;
          }

          onPick(file);
        }}
      />
      <button
        className="btn"
        title="Enviar foto, vídeo ou arquivo"
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        📎
      </button>
    </>
  );
}

/**
 * O que impede este arquivo de ser enviado — `null` quando nada impede.
 *
 * Conferir aqui não substitui a conferência do servidor (o navegador é do
 * cliente, e o rótulo do tipo vem dele): serve para o atendente saber na hora,
 * em vez de esperar 30 MB subirem para receber uma recusa.
 *
 * Tipo em branco passa de propósito. É o que o navegador manda para `.csv`,
 * `.mkv` e para todo arquivo vindo de compartilhamento de rede — recusar aqui
 * bloquearia arquivo que o servidor aceita, porque lá a extensão também é olhada.
 */
export function attachmentProblem(file: File): string | null {
  if (file.size === 0) return 'O arquivo está vazio.';

  if (file.size > ATTACHMENT_MAX_BYTES) {
    return (
      `${file.name} tem ${formatBytes(file.size)}. ` +
      `O WhatsApp aceita até ${formatBytes(ATTACHMENT_MAX_BYTES)} por mensagem.`
    );
  }

  const declared = file.type?.trim();
  if (declared && !attachmentKindFor(declared)) {
    return (
      `Não é possível enviar ${file.name} pelo WhatsApp: ` +
      'página web (HTML, SVG), script e executável são recusados. Se precisar, compacte em ZIP.'
    );
  }

  return null;
}
